/**
 * The application root: engine + query cache, services, appearance, routes (web.md §3 "Routes"), the
 * dialog host, the toast and the shell's shortcuts.
 *
 *   /                                → the last view
 *   /f/:folderId[/m/:messageId]      reader on a folder
 *   /v/:viewId[/m/:messageId]        reader on a saved view
 *   /corrections[/m/:messageId]      corrected messages
 *   /settings/:category[/:recordId]  settings ('new' = new record, ?from= duplicates)
 *   /subscribe, /signin              account screens
 */

import { useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, createMemoryRouter, useLocation, useNavigate } from 'react-router-dom';
import { Onboarding } from '../account/Onboarding.js';
import { ModelOnboarding } from '../account/ModelOnboarding.js';
import { ModelEvents } from './model-events.js';
import { SignIn } from '../account/SignIn.js';
import { Subscribe } from '../account/Subscribe.js';
import { registerSettingsDialogs } from '../settings/shell/dialogs.js';
import type { EngineClient } from '../engine-client/client.js';
import { EngineProvider, useEngineStatus } from '../engine-client/EngineProvider.js';
import { applyAppearance } from '../theme/apply.js';
import { registerBuiltinDialogs } from './builtin-dialogs.js';
import { DialogHost, openDialog } from './dialogs.js';
import { useEntitlement, useLocalSettings, useSettings } from './hooks.js';
import { lastReaderPath } from './navigation.js';
import { createAppServices, ServicesProvider, type AppServices } from './services.js';
import { installShortcuts, useShortcut } from './shortcuts.js';
import { Toast } from './Toast.js';
import { ui } from './ui-store.js';
import { Workspace } from './Workspace.js';

registerBuiltinDialogs();
registerSettingsDialogs();

/** Writes the synced appearance (and the plan's theme limits) to the document. */
function AppearanceApplier() {
  const settings = useSettings();
  const entitlement = useEntitlement();
  useLayoutEffect(() => {
    if (!settings || !entitlement) return;
    applyAppearance({ appearance: settings.appearance, reading: settings.reading, customTheme: settings.customTheme, limits: entitlement.limits });
  }, [settings, entitlement]);
  return null;
}

/** Loads the persisted UiState (Details open, sidebar collapsed) once. */
function UiStateLoader() {
  const local = useLocalSettings();
  const done = useRef(false);
  useEffect(() => {
    if (!local || done.current) return;
    done.current = true;
    ui.set({ detailsOpen: local.ui.detailsOpen, sidebarCollapsed: local.ui.sidebarCollapsed });
  }, [local]);
  return null;
}

function HomeRedirect() {
  const local = useLocalSettings();
  if (!local) return null;
  const last = local.ui.lastView;
  return <Navigate to={last.kind === 'view' ? `/v/${encodeURIComponent(last.id)}` : `/f/${encodeURIComponent(last.id)}`} replace />;
}

/** The shell's own shortcuts (web.md §6): /, comma, Escape, Ctrl/Cmd+S, ?. */
function ShellShortcuts() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const pathRef = useRef(routerLocation.pathname);
  pathRef.current = routerLocation.pathname;
  const inSettings = (): boolean => pathRef.current.startsWith('/settings');
  useEffect(() => installShortcuts(), []);
  useShortcut('/', () => document.getElementById('global-search')?.focus(), { description: 'Search' });
  useShortcut(',', () => navigate('/settings/sources'), { description: 'Settings', when: () => !inSettings() });
  useShortcut('mod+s', () => void ui.get().draft?.save(), { description: 'Save the settings editor', when: inSettings });
  useShortcut('?', () => openDialog('shortcuts', {}), { description: 'Shortcut list' });
  useShortcut(
    'Escape',
    () => {
      if (ui.get().mobileOverlay !== 'none') {
        ui.set({ mobileOverlay: 'none' });
        return;
      }
      const path = pathRef.current;
      if (path.startsWith('/settings')) navigate(lastReaderPath());
      else {
        const m = path.indexOf('/m/');
        if (m > 0) navigate(path.slice(0, m));
      }
    },
    { description: 'Back' },
  );
  return null;
}

function RootLayout() {
  return (
    <>
      <Outlet />
      <ShellShortcuts />
      <Onboarding />
      <ModelOnboarding />
      <ModelEvents />
      <DialogHost />
      <Toast />
    </>
  );
}

const routes = [
  {
    element: <RootLayout />,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: '/f/:folderId/*', element: <Workspace mode="reader" /> },
      { path: '/v/:viewId/*', element: <Workspace mode="reader" /> },
      { path: '/corrections/*', element: <Workspace mode="reader" /> },
      { path: '/settings', element: <Navigate to="/settings/sources" replace /> },
      { path: '/settings/:category/:recordId?', element: <Workspace mode="settings" /> },
      { path: '/subscribe', element: <Subscribe /> },
      { path: '/signin', element: <SignIn /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

/** A full-page error when the engine cannot start (e.g. storage_unavailable). */
function HostErrorGate({ children }: { children: ReactNode }) {
  const status = useEngineStatus();
  if (!status.hostError) return <>{children}</>;
  return (
    <main className="host-error" role="alert">
      <h1>Digester cannot open its database</h1>
      <p>{status.hostError.message}</p>
      <p className="field-help">Close other Digester tabs or windows, then reload.</p>
      <button type="button" className="primary-button" onClick={() => location.reload()}>
        Reload
      </button>
    </main>
  );
}

export interface AppProps {
  client: EngineClient;
  services?: AppServices;
  /** Tests: an in-memory router starting here instead of the browser URL. */
  initialPath?: string;
}

export function App({ client, services, initialPath }: AppProps) {
  const router = useMemo(() => (initialPath ? createMemoryRouter(routes, { initialEntries: [initialPath] }) : createBrowserRouter(routes)), [initialPath]);
  const appServices = useMemo(() => services ?? createAppServices(client.engine), [client, services]);
  return (
    <EngineProvider client={client}>
      <ServicesProvider services={appServices}>
        <AppearanceApplier />
        <UiStateLoader />
        <HostErrorGate>
          <RouterProvider router={router} />
        </HostErrorGate>
      </ServicesProvider>
    </EngineProvider>
  );
}
